import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { getConfig } from 'modules/App/config';
import { useInvalidateAndUpdate } from 'modules/App/hooks/useInvalidateAndUpdate';
import { selectIsMobile } from 'modules/App/store/selectors';
import getAsyncTaskStatus from 'modules/AsyncTask/services/getAsyncTaskStatus';
import { notificationsMessages } from 'modules/Documents/List/messages';
import { invalidateQueries as fetchQuery } from 'modules/Documents/List/services/fetchDocumentsToReview';
import scanBulkDocumentsService from 'modules/Documents/List/services/scanBulkDocuments';
import { invalidateQueries as scanRemindersQuery } from 'modules/Documents/Upload/services/getRemainingScans';
import useTranslations from 'modules/I18n/hooks/useTranslations';
import { Dialog, useCloseModal } from 'modules/Modals';
import useNotifications from 'modules/Notifications/hooks/useNotifications';
import { Box, Text } from 'modules/Ui';
import { IconStateDone, IconScan } from 'modules/Ui/Icons';
import updateNotification from 'modules/UserNotifications/List/services/updateNotification';
import { ROUTES } from 'pages/documents/routesMap';
import { useBlockingMutation } from 'utils/useBlockingMutation';

import { scanningDocumentBulk } from './messages';
import { ScanningDocumentBulkModalProps } from './types';

const ScanningDocumentBulkModal: FC<ScanningDocumentBulkModalProps> = ({
  excludeIds,
  ids,
}) => {
  const abortController = new AbortController();
  const onCancel = () => {
    abortController.abort();
  };
  const [finished, setFinished] = useState(false);

  const closeModal = useCloseModal();
  const notifications = useNotifications();

  const invalidate = useInvalidateAndUpdate();
  const invalidateQueries = [...fetchQuery, ...scanRemindersQuery];

  const { mutate: scanBulkDocumentsMutation } = useBlockingMutation(
    scanBulkDocumentsService,
    {
      onSuccess: async (bulkRequestResult: { taskId: string }) => {
        const appConfig = await getConfig();

        invalidate({ invalidateQueries });
        try {
          const asyncTaskStatusResult = await getAsyncTaskStatus(
            bulkRequestResult.taskId,
            {
              cancelRequestOnDemand: abortController,
              interval: Number(appConfig.zipPollingInterval),
              timeout: Number(appConfig.zipPollingTimeout),
            }
          );
          invalidate({ invalidateQueries });
          const notificationId = asyncTaskStatusResult?.result?.notificationId;

          if (notificationId) {
            await updateNotification({
              id: notificationId,
              read: true,
              sent: true,
            });
          }

          setFinished(true);
          setTimeout(() => {
            closeModal();
          }, 3000);
        } catch (error) {
          closeModal();
        }
      },
      onError: () => {
        notifications.error(notificationsMessages.scanDocumentsError.id);
        closeModal();
      },
    }
  );

  useEffect(() => {
    scanBulkDocumentsMutation({
      excludeIds,
      ids,
    });

    return () => {
      onCancel();
    };
  }, []);

  const {
    t,
    formatHelpers: { br },
  } = useTranslations();
  const isMobile = useSelector(selectIsMobile);
  const history = useHistory();

  const subtitle = finished
    ? t(scanningDocumentBulk.scannedDocument)
    : t(scanningDocumentBulk.scanningDocument);

  const text = finished
    ? t(scanningDocumentBulk.scannedDocumentText)
    : t(scanningDocumentBulk.scanningDocumentText, {
        br,
      });

  const onClose = () => {
    onCancel();
    history.push(`${ROUTES.DOCUMENTS_EXPENSES}?expenseType=TO_REVIEW`);
    closeModal();
  };

  return (
    <Dialog
      id="scanning-document-bulk"
      underlayMarginTop={isMobile ? '176px' : '96px'}
      closeOnClickOutside={true}
      onClose={onClose}
      title={t(scanningDocumentBulk.title)}
      dialogWidth={{ sm: '604px' }}
    >
      <Box display="flex" flexDirection="column" alignItems="center">
        {finished ? (
          <IconStateDone marginBottom="24px" size={64} />
        ) : (
          <IconScan isAnimate marginBottom="24px" size={64} />
        )}
        <Text
          color="gray800"
          fontSize={16}
          lineHeight={22}
          fontWeight="600"
          marginBottom="16px"
          textAlign="center"
        >
          {subtitle}
        </Text>
        <Text color="gray600" fontSize={14} lineHeight={22} textAlign="center">
          {text}
        </Text>
      </Box>
    </Dialog>
  );
};

export default ScanningDocumentBulkModal;
